import React, { useEffect, useRef, useState } from "react";
import { Button, Col, Form, Input, Modal, Row, Segmented } from "antd";
import {
  DeploymentUnitOutlined,
  ExclamationCircleFilled,
  PartitionOutlined,
  TableOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import PageHeader from "../generic/PageHeader";
import { showError, showSuccess, showIssues } from "../../helper/notify";
import { getAllTasks } from "../../service/task.service";
import { getAllSuiteData } from "../../service/suite.service";
import { getAllUISuites } from "../../service/uitest.service";
import {
  getAllEnvironment,
  getDataSubTypeByType,
} from "../../service/admin.service";
import {
  createNewMasterSuite,
  createNewMasterSuiteSteps,
  getMasterSuiteLibrary,
  getMasterSuiteStepsByName,
} from "../../service/mastersuite.service";
import { useUser } from "../../context/UserContext";
import DropdownSelect from "../../components/generic/DropdownSelect";
import MasterSuiteNewTable from "../../pages/mastersuite/MasterSuiteNewTable";
import MasterSuiteViewTable from "../../pages/mastersuite/MasterSuiteViewTable";
import MasterSuiteFlow from "./MasterSuiteFlow";
import ErrorBoundary from "../generic/ErrorBoundary";
import { healSelfParents } from "../../helper/masterSuiteTree";
import StepEditDrawer from "./StepEditDrawer";
import { toFlowNodes } from "../../helper/masterSuiteFlow";
import VariablesViewModal from "../../pages/mastersuite/variables/VariablesViewModal";
import VariablesModal from "../../pages/mastersuite/variables/VariablesModal";
import TaskModal from "../../pages/task/TaskModal";
import TaskViewModal from "../../pages/task/TaskViewModal";
import { useMasterSuiteStepEditor } from "../../pages/mastersuite/useMasterSuiteStepEditor";
import { useMediaQuery } from "../../helper/useMediaQuery";

// Shared shell for the Master Suite "workflow" pages — workflow canvas on top,
// table below. mode="create" (editable) | "view" (read-only). The create logic
// is moved verbatim from the former MasterSuiteNewModal — no functional change.
function MasterSuiteWorkspace({ mode = "create", name, defaultEnvironment: envProp }) {
  const isView = mode === "view";
  const navigate = useNavigate();
  const { email } = useUser();

  const [displayName, setDisplayName] = useState(isView ? name || "" : "");
  const [defaultEnvironment, setDefaultEnvironment] = useState(envProp || "");
  // create: rows pushed up from the editable table; view: fetched step rows
  const [masterSuiteTable, setMasterSuiteTable] = useState([]);

  // create-only reference data
  const [taskList, setTaskList] = useState([]);
  const [suiteList, setSuiteList] = useState([]);
  const [uiSuiteList, setUiSuiteList] = useState([]);
  const [environments, setEnvironments] = useState([]);
  const [environmentFolderPath, setEnvironmentsFolderPath] = useState([]);
  const [libraryData, setLibraryData] = useState([]);
  const [newlyCreatedTask, setNewlyCreatedTask] = useState(null);
  // View-mode: step clicked in the canvas → read-only step/task/parameters modal.
  const [detailStep, setDetailStep] = useState(null);
  // Create-mode: which editing surface, and the node-edit drawer state.
  const [editorView, setEditorView] = useState("Workflow");
  const [drawerKey, setDrawerKey] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Width-driven default view (canvas doesn't reflow — it scrolls/zooms).
  // <768: Table only, toggle hidden · 768–1024: default Table · ≥1024: default
  // Workflow. An explicit user toggle wins over later width changes.
  const isNarrow = useMediaQuery("(max-width: 1024px)");
  const isMobile = useMediaQuery("(max-width: 768px)");
  const userChoseViewRef = useRef(false);
  useEffect(() => {
    if (userChoseViewRef.current) return;
    setEditorView(isNarrow ? "Table" : "Workflow");
  }, [isNarrow]);
  const effectiveView = isMobile ? "Table" : editorView;

  const resetNewlyCreatedTask = () => setNewlyCreatedTask(null);
  const refreshTaskData = (newTask = null) => {
    getAllTasks().then((res) => {
      setTaskList(res.data);
      if (newTask) setNewlyCreatedTask(newTask);
    });
  };

  // Single source of truth for the steps being built (create-mode).
  const editor = useMasterSuiteStepEditor({
    taskList,
    testSuiteList: suiteList,
    uiSuiteList,
    environments,
    folderPaths: environmentFolderPath,
    defaultEnvironment,
    refreshTaskData,
    newlyCreatedTask,
    resetNewlyCreatedTask,
  });

  // Steps feeding the canvas + save: editor rows in create, fetched rows in view.
  const steps = isView ? masterSuiteTable : editor.dataSource;

  // Every "add" opens a DRAFT. Nothing reaches the graph until the drawer is saved, so an
  // abandoned add costs nothing — that is what stops a cancelled step leaving an orphan.
  // `intent` records where the step would land, and the hook applies it on commit.
  const openDraft = (intent) => {
    setDrawerKey(editor.openDraftForNew(intent));
    setDrawerOpen(true);
  };

  const handleAddStep = () => openDraft({ type: "append" });
  // childKey (the step on the far side of the clicked cable) lets the new step take over
  // that step's place in the chain — applied on commit, once it has a name.
  const handleInsertStep = (index, childKey) =>
    openDraft({ type: "insert", index, childKey });

  // Hover "+" on a card — opens the same drawer as every other add.
  const handleAddChildStep = (parentKey) => openDraft({ type: "child", parentKey });

  const handleAddPreset = (preset) => {
    // Use the key openDraftForNew RETURNS — editor.draft is still the previous value in
    // this tick, so reading it here would seed the wrong row (or none).
    const key = editor.openDraftForNew({ type: "append" });
    setDrawerKey(key);
    setDrawerOpen(true);
    // Same field handlers the drawer uses, so the seed lands on the draft, not the graph.
    const rec = { key };
    if (preset.type) editor.handleTypeChange(preset.type, rec);
    if (preset.category && preset.type !== "Master Suite")
      editor.handleCategoryChange(preset.category, rec);
    if (preset.subcategory && preset.category !== "ui")
      editor.handleSubcategoryChange(preset.subcategory, rec);
  };

  // Closing ALWAYS discards. There is never a half-written step in the graph to clean up.
  const closeDrawer = () => {
    editor.discardDraft();
    setDrawerOpen(false);
    setDrawerKey(null);
  };

  const handleNodeClick = (node) => {
    if (isView) {
      setDetailStep(node.raw);
    } else {
      setDrawerKey(editor.openDraftForEdit(node.raw.key));
      setDrawerOpen(true);
    }
  };

  // Self-fetch on mount so the page survives refresh / deep-link.
  useEffect(() => {
    if (isView) {
      getMasterSuiteStepsByName(name)
        // healSelfParents: a suite saved before the rename guard existed can carry a step
        // whose parent is its own name. The canvas only tolerates it, so repair on load.
        .then((res) => setMasterSuiteTable(healSelfParents(res.data || [])))
        .catch(() => showError("Couldn't load the master suite's steps."));
      if (!envProp) {
        getMasterSuiteLibrary()
          .then((res) => {
            const row = (res.data || []).find((r) => r.name === name);
            if (row) setDefaultEnvironment(row.defaultEnvironment);
          })
          .catch(() => {});
      }
      return;
    }
    getAllTasks().then((r) => setTaskList(r.data));
    getAllSuiteData().then((r) => setSuiteList(r.data));
    getAllEnvironment().then((r) => setEnvironments(r.data));
    getDataSubTypeByType("Environment").then((r) =>
      setEnvironmentsFolderPath(
        (r.data || []).filter((item) => item.name === "Folder Path"),
      ),
    );
    getAllUISuites()
      .then((r) => setUiSuiteList(r.data || []))
      .catch(() => setUiSuiteList([]));
    getMasterSuiteLibrary()
      .then((r) => setLibraryData(r.data || []))
      .catch(() => setLibraryData([]));
  }, [isView, name, envProp]);

  const displayNamePresent = libraryData.some(
    (item) => item.name.toLowerCase() === displayName?.toLocaleLowerCase(),
  );

  const onSave = () => {
    if (!displayName || displayNamePresent) {
      showError(
        !displayName
          ? "Display name cannot be blank."
          : "Display name already exists. Please choose another.",
      );
      return;
    }
    if (!defaultEnvironment) {
      showError("Default environment is required.");
      return;
    }
    if (steps.length === 0) {
      showError("Add at least one step before saving.");
      return;
    }

    const stepReferenceSet = new Set();
    const errors = steps.reduce(
      (acc, { category, subcategory, type, taskName, stepReference }, index) => {
        const label = stepReference
          ? `Step ${index + 1} "${stepReference}"`
          : `Step ${index + 1}`;
        if (!type) acc.push(`${label}: choose a Type`);
        if (!category || !subcategory)
          acc.push(`${label}: Category and Sub Category are required`);
        if (type === "task" && !taskName)
          acc.push(`${label}: Task Name is required`);
        if (!stepReference)
          acc.push(`Step ${index + 1}: enter a Step name`);
        if (stepReferenceSet.has(stepReference)) {
          acc.push(`Duplicate step name "${stepReference}"`);
        } else {
          stepReferenceSet.add(stepReference);
        }
        return acc;
      },
      [],
    );
    if (errors.length > 0) {
      showIssues("Can't save this master suite", errors);
      return;
    }

    // pendingAdoptChildKey is builder-only bookkeeping for an insert-between that hasn't
    // been named yet — it must not travel to the backend.
    const masterSuiteStepData = steps.map(
      ({ pendingAdoptChildKey, ...step }) => ({
        ...step,
        name: displayName,
      }),
    );

    createNewMasterSuite(
      displayName,
      "comments implement",
      email,
      defaultEnvironment,
    ).then(() => {
      createNewMasterSuiteSteps(masterSuiteStepData, displayName).then(() => {
        showSuccess("Master Suite created successfully");
        navigate("/mastersuite");
      });
    });
  };

  const isDirty = () =>
    !isView && (displayName.trim() !== "" || editor.dataSource.length > 0);

  // Leave back to the library — prompts only when there are unsaved changes.
  const leave = () => {
    if (!isDirty()) {
      navigate("/mastersuite");
      return;
    }
    Modal.confirm({
      title: "Discard unsaved changes?",
      icon: <ExclamationCircleFilled style={{ color: "#d48806" }} />,
      content:
        "This master suite hasn't been saved yet. If you leave now, the steps you've added will be lost.",
      okText: "Discard changes",
      okButtonProps: { danger: true },
      cancelText: "Keep editing",
      centered: true,
      onOk() {
        navigate("/mastersuite");
      },
    });
  };

  return (
    <div className="db-container">
      <PageHeader
        {...(isView ? { backTo: -1 } : { onBack: leave })}
        icon={<DeploymentUnitOutlined />}
        title={isView ? displayName || "Master Suite" : "Create Master Suite"}
        subtitle={
          isView
            ? defaultEnvironment
              ? `Environment · ${defaultEnvironment}`
              : "Read-only"
            : "Build a workflow of steps, then save"
        }
        actions={
          !isView && (
            <Button type="primary" onClick={onSave}>
              Save
            </Button>
          )
        }
      />

      <Row gutter={16} style={{ marginBottom: 8 }}>
        <Col span={12}>
          <p className="spec-modal-label">Display Name *</p>
          <Form layout="vertical">
            <Form.Item
              validateStatus={!isView && displayNamePresent ? "error" : ""}
              help={
                !isView && displayNamePresent
                  ? "Please choose a different display name"
                  : ""
              }
            >
              <Input
                placeholder="display name"
                value={displayName}
                disabled={isView}
                onChange={(e) => setDisplayName(e.target.value)}
              />
            </Form.Item>
          </Form>
        </Col>
        <Col span={12}>
          <p className="spec-modal-label">Default Environment *</p>
          {isView ? (
            <Input value={defaultEnvironment} disabled />
          ) : (
            <DropdownSelect
              placeholder="select environment"
              labelKey="displayName"
              valueKey="displayName"
              options={environments}
              onSelect={(value) => setDefaultEnvironment(value)}
              value={defaultEnvironment}
            />
          )}
        </Col>
      </Row>

      {!isView && !isMobile && (
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
          <Segmented
            value={effectiveView}
            onChange={(v) => {
              userChoseViewRef.current = true;
              setEditorView(v);
            }}
            options={[
              { label: "Workflow", value: "Workflow", icon: <PartitionOutlined /> },
              { label: "Table", value: "Table", icon: <TableOutlined /> },
            ]}
          />
        </div>
      )}

      {/* The canvas is the riskiest thing on this page: a lot of geometry, recomputed on
          every keystroke, over steps that are half-typed by definition. Contain a crash
          here so the editor state — which lives in this component — survives and the user
          can finish in the Table view rather than losing unsaved steps to a blank page. */}
      <ErrorBoundary
        title="The workflow diagram couldn't be drawn"
        hint="Your steps are safe. Switch to Table view to keep editing, or try again."
      >
      <MasterSuiteFlow
        nodes={toFlowNodes(steps, "builder")}
        mode={!isView && effectiveView === "Workflow" ? "edit" : "view"}
        title="Workflow"
        subtitle={
          isView
            ? "Steps run in this order — click a step to view its task and parameters"
            : "Steps run in this order — click a step to edit it"
        }
        activeKey={!isView && drawerOpen ? drawerKey : undefined}
        onAddStep={handleAddStep}
        onAddPreset={handleAddPreset}
        onInsertAt={handleInsertStep}
        // Canvas drag re-parents; the Table view keeps editor.onDragEnd for reordering
        // siblings, which is still how sequence is changed.
        onAddChild={handleAddChildStep}
        onReparent={editor.handleReparent}
        onNodeClick={handleNodeClick}
      />
      </ErrorBoundary>

      {isView ? (
        <MasterSuiteViewTable
          dataSource={masterSuiteTable}
          environment={defaultEnvironment}
        />
      ) : (
        effectiveView === "Table" && <MasterSuiteNewTable editor={editor} />
      )}

      {/* View-mode read-only step detail */}
      <VariablesViewModal
        open={!!detailStep}
        step={detailStep}
        record={detailStep?.variables || {}}
        handleVariableModal={() => setDetailStep(null)}
      />

      {/* Create-mode editing surfaces — drawer + shared Parameters/Task modals */}
      {!isView && (
        <>
          <StepEditDrawer
            editor={editor}
            open={drawerOpen}
            onClose={closeDrawer}
          />
          <VariablesModal {...editor.variablesModalProps} />
          <TaskModal {...editor.taskModalProps} />
          <TaskViewModal {...editor.taskViewModalProps} />
        </>
      )}
    </div>
  );
}

export default MasterSuiteWorkspace;
